import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer'
import { FC } from 'react'

import { AIPlanCampaignBuilderType, AIPlanStub } from 'api/interfaces/AIPlan'

Font.register({
  family: 'myriad-pro',
  fonts: [
    {
      src: require('../../../../../../assets/fonts/myriad-pro-regular.woff'),
      fontWeight: 400
    },
    {
      src: require('../../../../../../assets/fonts/myriad-pro-bold.woff'),
      fontWeight: 700
    }
  ]
})

Font.registerHyphenationCallback(word => [word])

interface AIPlanProjectionsPdfProps {
  stub: AIPlanStub
  chartImages: Record<string, string>
  isStaffPdf?: boolean
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    paddingTop: '1.25in',
    paddingLeft: '.75in',
    paddingBottom: '1.25in',
    paddingRight: '.75in',
    fontFamily: 'myriad-pro',
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  header: {
    fontFamily: 'myriad-pro',
    fontSize: '26pt',
    lineHeight: 1.2,
    marginBottom: '10pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%',
    textAlign: 'center'
  },
  section: {
    marginBottom: 25,
    pageBreakInside: 'avoid'
  },
  sectionTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '17pt',
    lineHeight: 1.2,
    marginTop: '30pt',
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  chartImage: {
    width: '100%',
    maxHeight: 400,
    marginBottom: 20,
    border: '1px solid #e9ecef'
  },
  description: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    textAlign: 'left',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  subsectionTitle: {
    fontFamily: 'myriad-pro',
    fontSize: '15pt',
    lineHeight: 1.2,
    marginBottom: '4pt',
    fontWeight: 700,
    wordWrap: 'break-word',
    maxWidth: '100%'
  },
  pageBreak: {
    pageBreakBefore: 'always'
  },
  columnsContainer: {
    flexDirection: 'row',
    gap: 20,
    marginTop: 20,
    marginBottom: '.75in',
    alignItems: 'flex-start'
  },
  column: {
    flex: 1,
    paddingHorizontal: 10
  },
  columnHeader: {
    minHeight: 110
  },
  columnImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  placeholderContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
    backgroundColor: '#f8f9fa',
    border: '1px dashed #dee2e6'
  },
  noDataText: {
    fontFamily: 'myriad-pro',
    fontSize: '12pt',
    lineHeight: 1.6,
    marginBottom: '4pt',
    fontWeight: 400,
    wordWrap: 'break-word',
    maxWidth: '100%',
    fontStyle: 'italic',
    color: '#6c757d'
  },
  sectionBox: {
    backgroundColor: '#E6EDF2',
    borderRadius: '8pt'
  }
})

const AIPlanProjectionsPdf: FC<AIPlanProjectionsPdfProps> = ({ isStaffPdf, stub, chartImages }) => {
  const hasInternet = stub.campaignBuilders.some(cb => cb.campaignType === AIPlanCampaignBuilderType.Internet)
  const hasTv = stub.campaignBuilders.some(cb => cb.campaignType === AIPlanCampaignBuilderType.TV)
  const hasBillboard = stub.campaignBuilders.some(cb => cb.campaignType === AIPlanCampaignBuilderType.Billboard)

  const CTVPlacementCategories = () => {
    return (
      <>
        <View style={styles.columnHeader}>
          <Text style={styles.subsectionTitle}>CTV: Placement Categories</Text>
          <Text style={{ ...styles.description, marginBottom: 0 }}>Based on chosen audience and historic delivery data, CTV impressions are projected to appear across premium outlets in the following categories. Precise distribution may vary based on live auction constraints.</Text>
        </View>
        <View style={styles.sectionBox}>
          {hasTv && chartImages.connectedTv ? (
            <Image src={chartImages.connectedTv} style={{ ...styles.columnImage, paddingLeft: '20', paddingRight: '20' }} />
          ) : (
            <View style={styles.placeholderContent}>
              <Text style={styles.noDataText}>CTV data not available</Text>
            </View>
          )}
        </View>
      </>
    )
  }

  const InternetAdPlacements = () => {
    return (
      <>
        <View style={styles.columnHeader}>
          <Text style={styles.subsectionTitle}>Internet Ad Placements</Text>
          <Text style={{ ...styles.description, marginBottom: 0 }}>Internet ads will be placed across any of the following sites, optimized for audience match.</Text>
        </View>
        <View style={styles.sectionBox}>
          {hasInternet && chartImages.internetDisplay ? (
            <Image src={chartImages.internetDisplay} style={{ ...styles.columnImage, paddingTop: '20', paddingBottom: '20' }} />
          ) : (
            <View style={styles.placeholderContent}>
              <Text style={styles.noDataText}>Internet display data not available</Text>
            </View>
          )}
        </View>
      </>
    )
  }

  const BlankPageIfStaffPdf = () => {
    return isStaffPdf ? (
      <Page size='LETTER' orientation='landscape' style={styles.page}>
        <View style={styles.section} />
      </Page>
    ) : null
  }

  return (
    <Document
      title="Projections"
      author="AdCritter"
      creator="AdCritter Platform"
      producer="React-PDF">
      <Page size='LETTER' orientation='landscape' style={styles.page} wrap={true}>
        <View style={styles.section} wrap={false}>
          <Text style={styles.header}>Projections</Text>
        </View>

        {chartImages.deliveryMetrics && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>CPM and Impressions</Text>
            <Text style={styles.description}>CPM and impression projections are based on real-time publisher data and historic campaign performance. The noted margin of error is due to inherent cost variation in digital ad auctions.</Text>
            <Image src={chartImages.deliveryMetrics} style={styles.chartImage} />
          </View>
        )}
      </Page>

      <BlankPageIfStaffPdf />

      <Page size='LETTER' orientation='landscape' style={styles.page} wrap={true}>
        {(hasInternet || hasTv) && (chartImages.connectedTv || chartImages.internetDisplay) && (
          <View style={[styles.section, styles.pageBreak]} wrap={false}>
            <Text style={styles.sectionTitle}>Where Ads Appear</Text>
            <Text style={styles.description}>Ad placements are made via auction-based ad exchanges. Bidding is optimized to prioritize audience and geographic accuracy over publisher allocation, so some variation in final distribution may occur.</Text>

            {hasTv && !hasInternet && chartImages.connectedTv && (
              <View style={styles.columnsContainer}>
                <View style={styles.column} wrap={false}>
                  <CTVPlacementCategories />
                </View>
                <View style={styles.column} wrap={false} />
              </View>
            )}

            {hasInternet && !hasTv && chartImages.internetDisplay && (
              <View style={styles.columnsContainer}>
                <View style={styles.column} wrap={false}>
                  <InternetAdPlacements />
                </View>
                <View style={styles.column} wrap={false} />
              </View>
            )}

            {hasInternet && hasTv && chartImages.connectedTv && chartImages.internetDisplay && (
              <View style={styles.columnsContainer}>
                <View style={styles.column} wrap={false}>
                  <CTVPlacementCategories />
                </View>
                <View style={styles.column} wrap={false}>
                  <InternetAdPlacements />
                </View>
              </View>
            )}
          </View>
        )}
      </Page>

      <BlankPageIfStaffPdf />

      <Page size='LETTER' orientation='landscape' style={styles.page} wrap={true}>
        {(hasInternet || hasTv) && (
          <>
            {chartImages.targetedHouseholds && (
              <View style={styles.section} wrap={false}>
                <Text style={styles.sectionTitle}>Targeted Household Locations</Text>
                <Text style={styles.description}>Projected distribution of views and impressions based on budget allocation, audience and demographic data.</Text>
                <Image src={chartImages.targetedHouseholds} style={styles.chartImage} />
              </View>
            )}
          </>
        )}
      </Page>

      {hasBillboard && (
        <>
          <BlankPageIfStaffPdf />

          <Page size='LETTER' orientation='landscape' style={styles.page} wrap={true}>
            {chartImages.billboardLocations && (
              <View style={[styles.section, hasInternet || hasTv ? styles.pageBreak : {}]} wrap={false}>
                <Text style={styles.sectionTitle}>Billboard Locations</Text>
                <Text style={styles.description}>Billboard ads will be placed programmatically via live auction. Placements are targeted by zip code and will appear on a selection of the following billboard sites. Choice of specific billboards within the set may be possible upon request, based on availability.</Text>
                <Image src={chartImages.billboardLocations} style={styles.chartImage} />
              </View>
            )}
          </Page>
        </>
      )}
    </Document>
  )
}

export default AIPlanProjectionsPdf
